import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import {
  NgCommonModule,
  ObservableDataSource,
  TrackByFunctions,
  extendSortingDataAccessor,
} from '@principle-theorem/ng-shared';
import { MeasureFormatter } from '@principle-theorem/principle-core/interfaces';
import {
  IDataPoint,
  IResolvedColumnProperty,
  ResolvedColumns,
} from '@principle-theorem/reporting';
import { filterUndefined, multiMap } from '@principle-theorem/shared';
import { first, isNumber, isString } from 'lodash';
import {
  BehaviorSubject,
  ReplaySubject,
  combineLatest,
  type Observable,
} from 'rxjs';
import { map, take } from 'rxjs/operators';
import { MetricPipe } from '../../../core/metric.pipe';
import { ReportBuilderStore } from '../../report-builder.store';
import { ReportBuilderResolvedColumnLabelComponent } from '../report-builder-resolved-column-label/report-builder-resolved-column-label.component';
import { ReportBuilderColumnManager } from './report-builder-column-manager';

@Component({
  selector: 'pr-report-builder-table',
  templateUrl: './report-builder-table.component.html',
  styleUrls: ['./report-builder-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ReportBuilderColumnManager],
  imports: [
    CdkDropListGroup,
    MatDivider,
    MatIcon,
    MatIconButton,
    MatMenuModule,
    MatPaginator,
    MatProgressSpinner,
    MatSortModule,
    MatTableModule,
    MatTooltip,
    MetricPipe,
    NgCommonModule,
    ReportBuilderResolvedColumnLabelComponent,
  ],
})
export class ReportBuilderTableComponent implements OnDestroy {
  private _sort$ = new ReplaySubject<MatSort>(1);
  trackByColumn =
    TrackByFunctions.nestedField<IResolvedColumnProperty>('definition.uid');
  dataSource: ObservableDataSource;
  columns$ = new BehaviorSubject<IResolvedColumnProperty[]>([]);
  columnIds$: Observable<string[]>;

  pageSizeOptions = [50, 100, 250, 500];
  previousIndex: number;
  isViewingHistoricalRun$: Observable<boolean>;

  @ViewChild(MatPaginator)
  set paginator(paginator: MatPaginator) {
    if (paginator) {
      this.dataSource.paginator = paginator;
    }
  }

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataSource.sort = sort;
    this._sort$.next(sort);
  }

  constructor(
    public store: ReportBuilderStore,
    public columnManager: ReportBuilderColumnManager // This could be an @Input
  ) {
    const filteredData$ = this.store.results$.pipe(
      map((results) => results?.filtered ?? [])
    );
    this.dataSource = new ObservableDataSource(filteredData$);
    this.isViewingHistoricalRun$ = this.store.activeHistoricalRun$.pipe(
      map((run) => !!run)
    );
    this.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );

    const selectedColumns$ = this.store.display$.pipe(
      map((display) => display?.columns ?? [])
    );

    combineLatest([
      this.store.dataSource$.pipe(filterUndefined()),
      selectedColumns$,
    ])
      .pipe(
        map(([dataSource, columns]) =>
          dataSource ? ResolvedColumns.toColumns(dataSource, columns) : []
        ),
        takeUntilDestroyed()
      )
      .subscribe((columns) => this.columns$.next(columns));

    this.columnIds$ = this.columns$.pipe(
      multiMap((column) => column.definition.uid)
    );

    combineLatest([this._sort$, this.columnIds$])
      .pipe(take(1), takeUntilDestroyed())
      .subscribe(([sort, columns]) => this._sortByFirstColumn(sort, columns));
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
  }

  getDataPoint(data: IGetDataPointRequest<unknown>): IDataPoint {
    const fact = data.row;
    const property = data.column.property;

    return {
      label: ResolvedColumns.getLabel(data.column),
      formatter: property.metadata.formatter,
      formatterValue: property.metadata.formatterValue,
      isPii: property.metadata.isPii,
      piiType: property.metadata.piiType,
      value: property.measure.dataAccessor(fact),
      valueLabel: property.measure.labelAccessor
        ? property.measure.labelAccessor(fact)
        : undefined,
    };
  }

  isLink(dataPoint?: IDataPoint | null): boolean {
    return dataPoint?.formatter === MeasureFormatter.Link;
  }

  hasValidLink(dataPoint?: IDataPoint | null): boolean {
    return !!dataPoint?.value;
  }

  isText(dataPoint?: IDataPoint | null): boolean {
    return dataPoint?.formatter === MeasureFormatter.Text;
  }

  private _sortingDataAccessor(
    record: unknown,
    sortHeaderId: string
  ): string | number | undefined {
    const column = this.columns$
      .getValue()
      .find((columnItem) => columnItem.definition.uid === sortHeaderId);

    if (!column) {
      return;
    }

    const value = column.property.measure.sortAccessor
      ? column.property.measure.sortAccessor(record)
      : column.property.measure.dataAccessor(record);

    if (isString(value) || isNumber(value)) {
      return value;
    }

    return value.toString();
  }

  private _sortByFirstColumn(sort: MatSort, columns: string[]): void {
    const firstColumnId = first(columns);
    if (!firstColumnId) {
      return;
    }
    sort.active = firstColumnId;
    sort.direction = 'asc';
    sort.sortChange.emit();
  }
}

interface IGetDataPointRequest<T> {
  row: Record<string, T>;
  column: IResolvedColumnProperty;
}
